import { DraggableHandle, SortableItem, VpdList } from "@design-system";
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { Typography } from "antd";
import React, { useState } from "react";
import styles from "./hierarchy-container.module.css";
import { sortBy } from "lodash";

type Props<T extends { id: number; description: string; order: number }> = {
  items: T[];
  onFinished: (items: T[]) => void;
};

export const UnitHierarchies = <T extends { id: number; description: string; order: number }>({
  items,
  onFinished,
}: Props<T>) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const [sortedItems, setItemsOrder] = useState<T[]>(items);

  const onReOrder = (activeId: number, overId: number) => {
    const activeIndex = sortedItems.findIndex((d) => d.id === activeId);
    const overIndex = sortedItems.findIndex((d) => d.id === overId);

    // Use arrayMove to reorder the items in the array
    const reorderedItems = arrayMove(sortedItems, activeIndex, overIndex);

    // Reassign order values based on new positions
    const updatedItems = reorderedItems.map((item, index) => ({
      ...item,
      order: index,
    }));

    setItemsOrder(updatedItems);
    onFinished(updatedItems);
  };

  const content = (
    <div className={styles.tab_content}>
      <DndContext
        onDragEnd={({ active, over }) => over && onReOrder(parseInt(active.id), parseInt(over.id))}
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={sortedItems.map((x) => x.id.toString())}
          strategy={verticalListSortingStrategy}
        >
          <VpdList
            items={sortBy(sortedItems, "order")}
            split
            itemKeyProperty="id"
            itemTextProperty="description"
            listStyle="primary"
            renderItem={(item) => (
              <SortableItem id={item.id.toString()} key={item.id}>
                {({ attributes, listeners }) => (
                  <VpdList.Item
                    contentPrefix={<DraggableHandle {...attributes} {...listeners} />}
                    key={item.id}
                    content={
                      <div className={styles.order_item}>
                        <Typography.Text editable={false}>{item.description}</Typography.Text>
                      </div>
                    }
                  ></VpdList.Item>
                )}
              </SortableItem>
            )}
          />
        </SortableContext>
      </DndContext>
    </div>
  );

  return content;
};
